/**
 * The account on the server (packages/server/src/account-routes.ts, team-routes.ts), as in KellerLog.
 * These calls need the network.
 */
import type { Role } from "@speisekarte/core";
import { api } from "./store.ts";
import type { Membership, SignedIn, User } from "./store.ts";

/**
 * Behind every place that announces a mail (Martin, 31.10.2026, in all the apps: mails from
 * noreply@mzapps.cloud land in the spam folder for some). Word for word as SpeisePlan's
 * docs/ANMELDUNG-EINHEITLICH.md § 13; SpeiseKarte speaks only German, so the English
 * („If no email arrives, check your spam folder.“) has no place here.
 */
export const SPAM_FOLDER = "Kommt keine Mail, sieh im Spam-Ordner nach.";
/** „Noch nicht bestätigt“ in „Mein Konto“ (§ 13; English „Not confirmed yet. Check your inbox, including the spam folder.“). */
export const NOT_CONFIRMED = "Noch nicht bestätigt. Sieh in deinem Postfach nach, auch im Spam-Ordner.";

export interface Session {
  id: string;
  device: string;
  createdAt: number;
  lastSeenAt: number;
  current: boolean;
}

export interface Passkey {
  id: string;
  name: string;
  createdAt: number;
  lastUsedAt: number | null;
  backedUp: boolean;
}

export interface TeamMember {
  id: string;
  displayName: string;
  role: Role;
  me: boolean;
  username?: string;
  hasEmail?: boolean;
  reset?: { expiresAt: number } | null;
  passkeys?: number;
}

export interface Invite {
  id: string;
  name: string;
  expiresAt: number;
  expired: boolean;
  role: Role;
}

export type LinkInfo =
  | { purpose: "join"; name: string; workspace: string; expiresAt: number }
  | { purpose: "reset"; name: string; username: string; expiresAt: number; passkeys: number };

/** The device's name in the server's list of sessions: enough to tell a phone from a laptop. */
export function deviceName(): string {
  const agent = navigator.userAgent;
  return /iPhone/.test(agent) ? "iPhone" : /iPad|Macintosh.*Mobile/.test(agent) ? "iPad" : /Android/.test(agent) ? "Android" : /Macintosh/.test(agent) ? "Mac" : /Windows/.test(agent) ? "Windows" : "Browser";
}

/** What this server offers; fetched once, the answer does not change while the app runs. */
let features: Promise<{ open: boolean; passkeys: boolean }> | null = null;
export const serverFeatures = () =>
  (features ??= api<{ open: boolean; passkeys?: boolean }>("/api/signup", {}, null)
    .then((r) => ({ open: r.open, passkeys: r.passkeys === true }))
    .catch((err) => {
      features = null;
      throw err;
    }));

/** Where the app lives (S30): the public address, whether this is an old one, the old one. */
export interface Site {
  origin: string;
  moved: boolean;
  oldOrigin: string | null;
}

let site: Promise<Site> | null = null;
export const siteInfo = () =>
  (site ??= api<Site>("/api/site", {}, null).catch((err) => {
    site = null;
    throw err;
  }));

/** "Montag, 21. Oktober" */
export const untilDay = (at: number) => new Date(at).toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" });

const id = (s: string) => encodeURIComponent(s);

export const team = {
  members: () => api<{ role: Role; members: TeamMember[]; invites: Invite[] }>("/api/workspace/members"),
  invite: (name: string, role: Role) => api<{ invite: Invite; url: string }>("/api/workspace/invites", { body: { name, role } }),
  setInviteRole: (inviteId: string, role: Role) => api(`/api/workspace/invites/${id(inviteId)}`, { method: "PATCH", body: { role } }),
  setRole: (userId: string, role: Role) => api(`/api/workspace/members/${id(userId)}`, { method: "PATCH", body: { role } }),
  renew: (inviteId: string) => api<{ invite: Invite; url: string }>(`/api/workspace/invites/${id(inviteId)}/link`, { body: {} }),
  withdraw: (inviteId: string) => api(`/api/workspace/invites/${id(inviteId)}`, { method: "DELETE" }),
  remove: (userId: string) => api(`/api/workspace/members/${id(userId)}`, { method: "DELETE" }),
  resetLink: (userId: string) => api<{ url: string; expiresAt: number }>(`/api/workspace/members/${id(userId)}/link`, { body: {} }),
  rename: (name: string) => api<{ name: string }>("/api/workspace/name", { method: "PUT", body: { name } }),
  check: (token: string) => api<LinkInfo>("/api/links/check", { body: { token } }, null),
  join: (token: string, body: { displayName: string; email: string; username: string; password: string }) => api<SignedIn>("/api/links/join", { body: { token, ...body, device: deviceName() } }, null),
  joinWithAccount: (token: string) => api<{ workspace: Membership; workspaces: Membership[] }>("/api/me/join", { body: { token } }, null),
  reset: (token: string, password: string, removePasskeys: boolean) => api<SignedIn>("/api/links/reset", { body: { token, password, removePasskeys, device: deviceName() } }, null),
};

export const account = {
  signupOpen: () => serverFeatures().then((f) => f.open),
  signup: (body: { workspace: string; displayName: string; email: string; username: string; password: string }) => api<SignedIn>("/api/signup", { body: { ...body, device: deviceName() } }, null),
  login: (username: string, password: string) => api<SignedIn>("/api/login", { body: { username, password, device: deviceName() } }, null),
  /** „Passwort vergessen?“: a link by mail; the same answer whether the account exists or not. */
  forgot: (who: string) => api<{ ok: true }>("/api/password/forgot", { body: { who } }, null),
  me: () => api<SignedIn & { session: string; demo?: boolean }>("/api/me"),
  setName: (displayName: string) => api<{ user: User }>("/api/me/name", { method: "PUT", body: { displayName } }),
  /** `sent`: a mail with the link to confirm went out (S28). */
  setEmail: (email: string) => api<{ user: User; sent: boolean }>("/api/me/email", { method: "PUT", body: { email } }),
  /** „Mail noch einmal schicken“: the link to confirm the address, again. */
  sendConfirm: () => api<{ user: User; sent: boolean }>("/api/me/email/send", { method: "POST" }),
  /** Confirms the address; only signed in as the account the link is for. */
  confirmEmail: (token: string) => api<{ user: User }>("/api/me/email/confirm", { body: { token } }),
  setPassword: (current: string, next: string) => api("/api/me/password", { method: "PUT", body: { current, next } }),
  sessions: () => api<{ sessions: Session[] }>("/api/me/sessions").then((r) => r.sessions),
  endSession: (sessionId: string) => api(`/api/me/sessions/${id(sessionId)}`, { method: "DELETE" }),
  passkeys: () => api<{ passkeys: Passkey[] }>("/api/me/passkeys").then((r) => r.passkeys),
  removePasskey: (passkeyId: string) => api(`/api/me/passkeys/${id(passkeyId)}`, { method: "DELETE" }),
  /** Under an old address (S30): the link that signs in under the new one, good for 60 seconds. */
  move: () => api<{ url: string }>("/api/move", { body: {} }, null),
  /** Under the new address: signs in with the key from the old one. */
  redeem: (key: string) => api<SignedIn>("/api/move/redeem", { body: { key, device: deviceName() } }, null),
  /** Whether it is the account's password; the form around it lets the password manager keep it here. */
  checkPassword: (password: string) => api<{ ok: true }>("/api/me/password/check", { body: { password } }, null),

  /** Creates a passkey on this device for the signed-in account. */
  async addPasskey(name: string): Promise<Passkey[]> {
    const { challenge, options } = await api<{ challenge: string; options: PublicKeyCredentialCreationOptionsJSON }>("/api/me/passkeys/options", { body: {} });
    const credential = (await navigator.credentials.create({ publicKey: PublicKeyCredential.parseCreationOptionsFromJSON(options) })) as PublicKeyCredential | null;
    if (!credential) throw new Error("Abgebrochen");
    return api<{ passkeys: Passkey[] }>("/api/me/passkeys", { body: { challenge, credential: credential.toJSON(), name } }).then((r) => r.passkeys);
  },

  /** Signs in with a passkey of this device, without user name. */
  async loginWithPasskey(): Promise<SignedIn> {
    const { challenge, options } = await api<{ challenge: string; options: PublicKeyCredentialRequestOptionsJSON }>("/api/passkeys/login/options", { body: {} }, null);
    const credential = (await navigator.credentials.get({ publicKey: PublicKeyCredential.parseRequestOptionsFromJSON(options) })) as PublicKeyCredential | null;
    if (!credential) throw new Error("Abgebrochen");
    return api<SignedIn>("/api/passkeys/login", { body: { challenge, credential: credential.toJSON(), device: deviceName() } }, null);
  },
};

/** Passkeys need a browser that reads the server's options as JSON (Safari 17.4, Chrome 129). */
export const passkeysSupported = () => typeof PublicKeyCredential !== "undefined" && typeof PublicKeyCredential.parseRequestOptionsFromJSON === "function" && window.isSecureContext;

/** A cancelled passkey dialog is no error worth showing. */
export const cancelled = (err: unknown) => err instanceof DOMException && (err.name === "NotAllowedError" || err.name === "AbortError");
